// Б-4: эвристики размещения юнитов ботом.
// Чистые статические правила без подсчёта урона / угроз.
//
// chooseLine(card, player, currentTurn) → 0 | 1 | null
//   0 — фронт (1-я линия), 1 — тыл (2-я линия), null — нет предпочтения.
//   Если предпочтительная линия полная (4 юнита) — возвращается противоположная.
//
// chooseSlot(card, line, player, rng) → idx (0..3) | null
//   Если есть свои юниты на линии — соседний слот (idx-1 / idx+1) с
//   существующим союзником. Если несколько одинаково соседних — random.
//   Если соседних нет — ближайший к существующему. Если линия пустая —
//   random среди свободных.
//
// Правила (диздок 6.4 / 6.6 / 7.3):
//   1-я линия (фронт):
//     - faction='global' (Глобал) — Подкрепление на 1-й линии даёт бонус атаки
//       (на 2-й — бонус hp). 1-я предпочтительнее для агрессивных юнитов.
//     - Высокая атака + низкое ХП (atk ≥ 3 и hp ≤ 4) — «бойцы».
//   2-я линия (тыл):
//     - Хилеры — защита от добивания (хилер на 1-й линии может быть убит
//       в раунде 1 Ф3 и не отработать раунд 2).
//     - Низкая атака + высокое ХП (atk ≤ 2 и hp ≥ 5) — «толстые блокеры».
//     - Спящие эпик/легенда (initiation > currentTurn) — беззащитны
//       в раннем розыгрыше, на 2-й линии безопаснее.
//   Без предпочтения — taunt (работает с любой линии) и прочие.

import { hasKeyword, KW } from '../engine/keywords.js';

// Жёсткий список cardId хилеров. Пока card.is_healer ставится только
// для Полевого медика Глобала через healer-registry. Хилеры Лив Лайф
// (хил-ауры) такого флага не имеют — их перечисляем здесь.
const HEALER_CARD_IDS = new Set([
  'glb_polevoy_medik',
  'liv_dron_spasatel',
  'liv_polevoy_sanitar',
  'liv_paramedik',
  'liv_ordinator'
]);

export function isHealerCard(card) {
  if (!card) return false;
  if (card.is_healer === true) return true;
  return HEALER_CARD_IDS.has(card.id);
}

function countFree(lineSlots) {
  let n = 0;
  for (let s = 0; s < 4; s++) if (lineSlots[s] === null) n++;
  return n;
}

// 0=front, 1=back, null=нет предпочтения. Не учитывает занятость линий.
//
// Порядок правил: специальные роли (хилер, спящий) → фракция Глобала →
// характеристики статов. Спящий эпик/легенда в раннем розыгрыше уязвим
// (не атакует, но ловит чужие удары) — для него 2-я линия безопаснее, даже
// если статы выглядят как «боец».
function preferredLine(card, currentTurn) {
  if (isHealerCard(card)) return 1;
  if ((card?.initiation ?? 0) > currentTurn) return 1;
  // C.1: Глобал — Подкрепление. Юниты с высокой атакой ставим на 1-ю (бонус atk),
  // с высоким hp — на 2-ю (бонус hp). Для остальных Глобал-юнитов оставляем общую логику.
  const atk = card?.base?.atk ?? 0;
  const hp  = card?.base?.hp  ?? 0;
  if (atk >= 3 && hp <= 4) return 0;
  if (atk <= 2 && hp >= 5) return 1;
  return null;
}

export function chooseLine(card, player, currentTurn) {
  const pref = preferredLine(card, currentTurn);
  const free0 = countFree(player.field[0]);
  const free1 = countFree(player.field[1]);
  if (free0 === 0 && free1 === 0) return null;        // нет места вообще
  if (pref === null) {
    // Нет предпочтения. Если на одной линии нет места — уйти на другую.
    if (free0 === 0) return 1;
    if (free1 === 0) return 0;
    return null;                                       // обе с местом — пусть caller выберет random
  }
  // Есть предпочтение.
  const altFree = pref === 0 ? free1 : free0;
  const prefFree = pref === 0 ? free0 : free1;
  if (prefFree > 0) return pref;
  if (altFree > 0) return 1 - pref;
  return null;
}

export function chooseSlot(card, line, player, rng) {
  const slots = [];
  for (let s = 0; s < 4; s++) if (player.field[line][s] === null) slots.push(s);
  if (slots.length === 0) return null;
  if (slots.length === 4) return slots[rng.int(4)];

  const allies = [];
  for (let s = 0; s < 4; s++) if (player.field[line][s] !== null) allies.push(s);
  if (allies.length === 0) return slots[rng.int(slots.length)];

  // Расстояние каждого свободного слота до ближайшего союзника.
  const distances = slots.map(s => {
    let min = Infinity;
    for (const a of allies) {
      const d = Math.abs(s - a);
      if (d < min) min = d;
    }
    return { slot: s, dist: min };
  });

  // Соседние слоты (dist === 1) — приоритет.
  const adjacent = distances.filter(x => x.dist === 1);
  if (adjacent.length > 0) {
    return adjacent[rng.int(adjacent.length)].slot;
  }

  // Соседних нет — ближайшие.
  let minDist = Infinity;
  for (const x of distances) if (x.dist < minDist) minDist = x.dist;
  const nearest = distances.filter(x => x.dist === minDist);
  return nearest[rng.int(nearest.length)].slot;
}
